/**
 * 창조신 김마고 - 메인 App 컴포넌트
 * 게임 코어 상태를 구독하여 렌더링만 담당
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../game/index.js';
import { useGameState } from '../hooks/useGameState.js';
import {
  BackendError,
  combineElements,
  fetchMe,
  fetchRankings,
  isAbortError,
  isBackendEnabled,
  registerUser,
  type RankingEntry,
  type RankingsResponse
} from '../api/backend.js';
import { MILESTONES, getNextMilestone, CATEGORY_ICONS, type ElementCategory } from '../game/content.js';
import { playSfx, preloadSfx } from '../sfx.js';
import { initializeAdMob, showInterstitial } from '../admob/interstitial.js';
import { preloadRewarded, showRewarded } from '../admob/rewarded.js';
import { MAX_ENERGY, ENERGY_RECHARGE_INTERVAL_MS } from '../game/state.js';

interface AppProps {
  core: GameCore<GameState, GameAction>;
}

const OPENING_PAGES = [
  '/comics/toon-1.jpeg',
  '/comics/toon-2.jpeg',
  '/comics/toon-3.jpeg',
  '/comics/toon-4.jpeg'
] as const;

const TIER_COLORS: Record<number, string> = {
  0: '#e0e0e0',
  1: '#a8d5ba',
  2: '#a2c2e8',
  3: '#c5a3e8',
  4: '#f3d081',
  5: '#f2a3a3',
  6: '#f2b5d4'
};

const COLORS = {
  bg: '#fdfbf7',
  card: '#ffffff',
  text: '#4a4238',
  textDim: '#8c8479',
  accent: '#d4b996',
  accentDim: '#b8a68d',
  accentLight: '#f5efe6',
  border: '#eee9e1',
  slotBg: '#f8f9fa'
};

const TOKEN_STORAGE_KEY = 'mago.auth_token';
const LOCAL_PROFILE_STORAGE_KEY = 'mago.local_profile.v1';
const DEFAULT_INVENTORY = ['불', '물', '흙', '바람'];

interface LocalProfileSnapshot {
  nickname: string;
  authToken: string | null;
  inventory: string[];
  hasSeenOpening: boolean;
  tutorialCompleted: boolean;
  energy?: number;
  lastEnergyRechargeAt?: number;
}

interface DialogMessage {
  title: string;
  message: string;
}

export function App({ core }: AppProps) {
  const state = useGameState(core);
  const backendEnabled = isBackendEnabled();
  const combineInFlightKeyRef = useRef<string | null>(null);
  const sessionBootstrappedRef = useRef(false);
  const [isRegisteringNickname, setIsRegisteringNickname] = useState(false);
  const [nicknameConfirmTarget, setNicknameConfirmTarget] = useState<string | null>(null);
  const [dialogMessage, setDialogMessage] = useState<DialogMessage | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [detailElement, setDetailElement] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const [showEnergyDialog, setShowEnergyDialog] = useState(false);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [rankings, setRankings] = useState<RankingsResponse | null>(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsSubTab, setRankingsSubTab] = useState<'discoveries' | 'first_discoveries' | 'max_depth'>('discoveries');
  const bgmRef = useRef<{
    ctx: AudioContext;
    gain: GainNode;
    source: AudioBufferSourceNode | null;
    buffers: Map<string, AudioBuffer>;
    currentPath: string;
    playing: boolean;
    startTime: number;
    pauseOffset: number;
  } | null>(null);

  const canStartFromTitle = state.screen === 'title';
  const nicknameTrimmed = state.nicknameDraft.trim();
  const isNicknameValid = /^[가-힣a-zA-Z0-9]{2,8}$/.test(nicknameTrimmed);

  const milestoneNames = useMemo(() => new Set(MILESTONES.map((m) => m.name)), []);
  const discoveredCount = state.inventory.filter((n) => !milestoneNames.has(n)).length;
  const nextMilestone = getNextMilestone(discoveredCount);
  const firstDiscoveryCount = state.firstDiscoveries.length;

  // 에너지 타이머 (다음 회복까지 남은 시간)
  const [energyTimerText, setEnergyTimerText] = useState('');
  useEffect(() => {
    if (state.energy >= MAX_ENERGY) {
      setEnergyTimerText('');
      return;
    }
    const tick = () => {
      const elapsed = Date.now() - state.lastEnergyRechargeAt;
      const remaining = ENERGY_RECHARGE_INTERVAL_MS - (elapsed % ENERGY_RECHARGE_INTERVAL_MS);
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setEnergyTimerText(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [state.energy, state.lastEnergyRechargeAt]);

  // AdMob 초기화
  useEffect(() => {
    initializeAdMob()
      .then(() => preloadRewarded())
      .catch((error) => {
        console.error('[App] Failed to initialize AdMob', error);
      });
  }, []);

  const filteredInventory = useMemo(() => {
    let filtered = state.inventory.filter((name) => name.includes(state.searchQuery.trim()));
    if (state.categoryFilter !== 'all') {
      filtered = filtered.filter((name) => {
        const el = state.elements[name];
        return el?.category === state.categoryFilter;
      });
    }
    if (state.sortMode === 'recent') return filtered;
    if (state.sortMode === 'name') {
      return [...filtered].sort((a, b) => a.localeCompare(b, 'ko-KR'));
    }
    return [...filtered].sort((a, b) => {
      const tierA = state.elements[a]?.tier ?? 0;
      const tierB = state.elements[b]?.tier ?? 0;
      if (tierA !== tierB) return tierB - tierA;
      return a.localeCompare(b, 'ko-KR');
    });
  }, [state.inventory, state.searchQuery, state.sortMode, state.categoryFilter, state.elements]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const name of state.inventory) {
      const cat = state.elements[name]?.category ?? '기타';
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [state.inventory, state.elements]);

  const unknownSlots = nextMilestone
    ? Math.max(0, nextMilestone.threshold - discoveredCount)
    : 0;

  const hasHuman = state.inventory.includes('인간');
  const showMissionBanner = !hasHuman && state.screen === 'main' && state.tutorialCompleted;

  const dispatch = (action: GameAction) => {
    core.dispatch(action);
  };

  // BGM 관리 (Web Audio API - 토스 WebView 호환)
  useEffect(() => {
    const BGM_MAP: Record<string, string> = {
      title: '/bgm/title-bgm.mp3',
      opening: '/bgm/cartoon-bgm.mp3',
      nickname: '/bgm/title-bgm.mp3',
      tutorial: '/bgm/main-bgm.mp3',
      main: '/bgm/main-bgm.mp3'
    };

    const targetPath = BGM_MAP[state.screen];
    if (!targetPath) return;

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    if (!bgmRef.current) {
      const ctx = new AudioCtx();
      const gain = ctx.createGain();
      gain.gain.value = 0.5;
      gain.connect(ctx.destination);
      bgmRef.current = { ctx, gain, source: null, buffers: new Map(), currentPath: '', playing: false, startTime: 0, pauseOffset: 0 };
    }

    const bgm = bgmRef.current;

    async function loadBuffer(path: string): Promise<AudioBuffer | null> {
      if (bgm.buffers.has(path)) return bgm.buffers.get(path)!;
      try {
        const resp = await fetch(path);
        const arr = await resp.arrayBuffer();
        const buf = await bgm.ctx.decodeAudioData(arr);
        bgm.buffers.set(path, buf);
        return buf;
      } catch { return null; }
    }

    function stopSource() {
      if (bgm.source) {
        try { bgm.source.stop(); } catch { /* already stopped */ }
        bgm.source.disconnect();
        bgm.source = null;
      }
      bgm.playing = false;
    }

    async function playTrack(path: string, fromOffset = 0) {
      if (bgm.ctx.state === 'suspended') {
        await bgm.ctx.resume();
      }
      const buffer = await loadBuffer(path);
      if (!buffer) return;
      stopSource();
      const src = bgm.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(bgm.gain);
      const offset = fromOffset % buffer.duration;
      src.start(0, offset);
      bgm.source = src;
      bgm.currentPath = path;
      bgm.startTime = bgm.ctx.currentTime - offset;
      bgm.pauseOffset = 0;
      bgm.playing = true;
    }

    // 트랙이 바뀌면 새 트랙 재생, 같으면 유지
    if (bgm.currentPath !== targetPath) {
      loadBuffer(targetPath); // 미리 로드
      if (bgm.playing || bgm.ctx.state === 'running') {
        playTrack(targetPath);
      }
    }

    // 첫 인터랙션에서 AudioContext resume + 재생
    const unlockAndPlay = async () => {
      if (bgm.ctx.state === 'suspended') {
        await bgm.ctx.resume();
      }
      if (!bgm.playing) {
        playTrack(targetPath);
      }
    };

    const events = ['touchstart', 'touchend', 'click', 'pointerdown'] as const;
    for (const e of events) {
      document.addEventListener(e, unlockAndPlay, { passive: true });
    }

    // 백그라운드 전환 대응
    const handleVisibility = () => {
      if (document.hidden) {
        if (bgm.playing) {
          bgm.pauseOffset = bgm.ctx.currentTime - bgm.startTime;
          stopSource();
        }
      } else {
        if (!bgm.playing && bgm.currentPath) {
          playTrack(bgm.currentPath, bgm.pauseOffset);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      for (const e of events) {
        document.removeEventListener(e, unlockAndPlay);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [state.screen]);

  const renderIcon = (icon: string, imageUrl?: string, size = 28) => {
    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt=""
          style={{
            width: `${size}px`,
            height: `${size}px`,
            objectFit: 'contain',
            borderRadius: `${Math.round(size * 0.15)}px`
          }}
        />
      );
    }
    return <span style={{ fontSize: `${size}px`, lineHeight: 1 }}>{icon}</span>;
  };

  useEffect(() => {
    preloadSfx();
  }, []);

  useEffect(() => {
    if (sessionBootstrappedRef.current) return;
    sessionBootstrappedRef.current = true;

    const savedProfile = parseLocalProfileSnapshot(window.localStorage.getItem(LOCAL_PROFILE_STORAGE_KEY));

    const restoreEnergy = (profile: LocalProfileSnapshot | null) => {
      if (!profile) return;
      const savedEnergy = profile.energy ?? MAX_ENERGY;
      const savedLastRecharge = profile.lastEnergyRechargeAt ?? Date.now();
      const now = Date.now();
      const elapsed = now - savedLastRecharge;
      const gained = Math.floor(elapsed / ENERGY_RECHARGE_INTERVAL_MS);
      const restoredEnergy = Math.min(savedEnergy + gained, MAX_ENERGY);
      const newLastRecharge = gained > 0 ? savedLastRecharge + gained * ENERGY_RECHARGE_INTERVAL_MS : savedLastRecharge;
      dispatch({ type: 'RECHARGE_ENERGY', payload: { amount: restoredEnergy - (state.energy ?? 0) } });
      dispatch({ type: 'TICK_ENERGY', payload: { now: newLastRecharge } });
    };

    const restoreFromLocal = (profile: LocalProfileSnapshot | null) => {
      if (!profile || !profile.nickname) return;
      dispatch({
        type: 'RESTORE_SESSION',
        payload: {
          nickname: profile.nickname,
          token: profile.authToken,
          inventory: profile.inventory.length > 0 ? profile.inventory : DEFAULT_INVENTORY,
          hasSeenOpening: profile.hasSeenOpening,
          tutorialCompleted: profile.tutorialCompleted
        }
      });
      restoreEnergy(profile);
    };

    if (!backendEnabled) {
      restoreFromLocal(savedProfile);
      return;
    }

    const token = savedProfile?.authToken ?? window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      restoreFromLocal(savedProfile);
      return;
    }

    let cancelled = false;
    fetchMe(token)
      .then((me) => {
        if (cancelled) return;
        dispatch({
          type: 'RESTORE_SESSION',
          payload: {
            nickname: me.nickname,
            token,
            inventory: me.inventory,
            hasSeenOpening: true,
            tutorialCompleted: true,
            firstDiscoveries: me.first_discoveries,
            serverElements: me.elements
          }
        });
        restoreEnergy(savedProfile);
      })
      .catch(() => {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        restoreFromLocal(savedProfile ? { ...savedProfile, authToken: null } : null);
      });

    return () => {
      cancelled = true;
    };
  }, [backendEnabled]);

  useEffect(() => {
    if (!state.nickname) return;

    const snapshot: LocalProfileSnapshot = {
      nickname: state.nickname,
      authToken: state.authToken,
      inventory: state.inventory,
      hasSeenOpening: state.hasSeenOpening,
      tutorialCompleted: state.tutorialCompleted,
      energy: state.energy,
      lastEnergyRechargeAt: state.lastEnergyRechargeAt
    };

    window.localStorage.setItem(LOCAL_PROFILE_STORAGE_KEY, JSON.stringify(snapshot));

    if (state.authToken) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, state.authToken);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, [state.nickname, state.authToken, state.inventory, state.hasSeenOpening, state.tutorialCompleted, state.energy, state.lastEnergyRechargeAt]);

  // 에너지 부족 감지: 양쪽 슬롯 채워졌는데 pendingCombine 없으면 에너지 부족
  useEffect(() => {
    if (state.slotA && state.slotB && !state.pendingCombine && state.energy < 1) {
      setShowEnergyDialog(true);
      dispatch({ type: 'REMOVE_SLOT', payload: { slot: 'B' } });
      dispatch({ type: 'REMOVE_SLOT', payload: { slot: 'A' } });
    }
  }, [state.slotA, state.slotB, state.pendingCombine, state.energy]);

  // 합성 시작 효과음 (슬롯 둘 다 채워졌을 때)
  useEffect(() => {
    if (state.pendingCombine?.status === 'animating') {
      playSfx('combine-start');
    }
  }, [state.pendingCombine?.status]);

  // 결과 팝업 효과음
  useEffect(() => {
    if (!state.resultPopup) return;
    const kind = state.resultPopup.kind;
    if (kind === 'first-discovery') playSfx('first-discovery');
    else if (kind === 'milestone') playSfx('milestone');
    else if (kind === 'discovered') playSfx('combine-success');
  }, [state.resultPopup]);

  useEffect(() => {
    const pending = state.pendingCombine;
    if (!pending) return;
    if (pending.status !== 'waiting-server') return;
    if (!backendEnabled || !state.authToken) return;

    const combineKey = `${pending.a}+${pending.b}`;
    if (combineInFlightKeyRef.current === combineKey) return;
    combineInFlightKeyRef.current = combineKey;

    combineElements(state.authToken, pending.a, pending.b)
      .then((response) => {
        dispatch({
          type: 'APPLY_SERVER_COMBINE_RESULT',
          payload: {
            a: pending.a,
            b: pending.b,
            result: response.result,
            tier: response.tier,
            category: response.category,
            description: response.description,
            depth: response.depth,
            isNew: response.is_new,
            isFirstEver: response.is_first_ever,
            firstDiscoverer: response.first_discoverer,
            imageUrl: response.image_url,
            discoveredCount: response.discovered_count
          }
        });
      })
      .catch((error) => {
        const message = error instanceof BackendError
          ? error.message
          : isAbortError(error)
            ? '서버 응답 시간이 초과되었습니다. 다시 시도해주세요.'
            : '합성 중 오류가 발생했습니다.';
        setDialogMessage({ title: '합성 실패', message });
        if (error instanceof BackendError && error.status === 401) {
          window.localStorage.removeItem(TOKEN_STORAGE_KEY);
          dispatch({ type: 'SET_AUTH_TOKEN', payload: { token: null } });
        }
        dispatch({ type: 'CLEAR_PENDING_COMBINE' });
      })
      .finally(() => {
        combineInFlightKeyRef.current = null;
      });
  }, [backendEnabled, state.pendingCombine, state.authToken]);

  const selectElement = (name: string) => {
    if (state.pendingCombine) return;
    playSfx('slot-place');
    dispatch({ type: 'SELECT_ELEMENT', payload: { name } });
  };

  const handleElementPointerDown = (name: string) => {
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      playSfx('ui-tap');
      setDetailElement(name);
    }, 600);
  };

  const handleElementPointerUp = (name: string) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (!longPressTriggeredRef.current) {
      selectElement(name);
    }
  };

  const handleElementPointerLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const removeSlot = (slot: 'A' | 'B') => {
    if (state.pendingCombine) return; // 조합 중에는 제거 불가
    dispatch({ type: 'REMOVE_SLOT', payload: { slot } });
  };

  const submitNickname = () => {
    if (!isNicknameValid || isRegisteringNickname) return;
    setNicknameConfirmTarget(nicknameTrimmed);
  };

  const confirmNickname = async () => {
    if (!nicknameConfirmTarget || isRegisteringNickname) return;

    setIsRegisteringNickname(true);
    try {
      if (backendEnabled) {
        const registered = await registerUser(nicknameConfirmTarget);
        window.localStorage.setItem(TOKEN_STORAGE_KEY, registered.token);
        dispatch({ type: 'SET_AUTH_TOKEN', payload: { token: registered.token } });
      }

      dispatch({ type: 'CONFIRM_NICKNAME', payload: { nickname: nicknameConfirmTarget } });
      setNicknameConfirmTarget(null);
    } catch (error) {
      const message = error instanceof BackendError ? error.message : '닉네임 등록 실패';
      setDialogMessage({ title: '닉네임 등록 실패', message });
    } finally {
      setIsRegisteringNickname(false);
    }
  };

  const popup = state.resultPopup;
  const popupTitle = popup?.kind === 'milestone'
    ? '레벨 업!'
    : popup?.kind === 'first-discovery'
      ? '🏆 최초 발견!! 🏆'
      : popup?.kind === 'already-owned'
        ? '이미 보유 중'
        : '✨ 발견! ✨';

  const slotElementA = state.slotA ? state.elements[state.slotA] : null;
  const slotElementB = state.slotB ? state.elements[state.slotB] : null;

  const loadRankings = async () => {
    if (!backendEnabled || rankingsLoading) return;
    setRankingsLoading(true);
    try {
      const data = await fetchRankings();
      setRankings(data);
    } catch {
      // silently fail
    } finally {
      setRankingsLoading(false);
    }
  };

  useEffect(() => {
    if (state.activeTab === 'rankings' && !rankings && backendEnabled) {
      loadRankings();
    }
  }, [state.activeTab]);

  const shareFirstDiscovery = async () => {
    if (!popup || popup.kind !== 'first-discovery') return;
    const text = `창조신 김마고에서 ${popup.result.name}을(를) 최초 발견했어요!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: '창조신 김마고', text });
        return;
      } catch {
        return;
      }
    }

    try {
      await navigator.clipboard.writeText(text);
      setDialogMessage({ title: '공유 준비 완료', message: '공유 문구를 클립보드에 복사했습니다.' });
    } catch {
      setDialogMessage({ title: '공유 문구', message: text });
    }
  };

  const renderSlot = (label: 'A' | 'B', elementName: string | null, element: GameState['elements'][string] | null) => {
    return (
      <button
        type="button"
        onClick={() => elementName && removeSlot(label)}
        style={{
          width: '46%',
          minHeight: '80px',
          border: element ? `2px solid ${TIER_COLORS[element.tier] ?? COLORS.border}` : `1px dashed ${COLORS.textDim}`,
          borderRadius: '16px',
          background: element ? COLORS.card : COLORS.slotBg,
          color: COLORS.text,
          fontWeight: 700,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          cursor: elementName ? (state.pendingCombine ? 'not-allowed' : 'pointer') : 'default',
          transition: 'all 0.2s ease',
          boxShadow: element ? '0 4px 12px rgba(74, 66, 56, 0.05)' : 'none'
        }}
      >
        <span style={{ fontSize: '11px', color: COLORS.textDim, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
        {element ? (
          <div 
            key={element.name}
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '4px',
              animation: 'mago-scale-up 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {renderIcon(element.icon, element.imageUrl, 28)}
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{element.name}</span>
          </div>
        ) : (
          <span style={{ color: COLORS.textDim, fontSize: '13px', fontWeight: 500 }}>선택하기</span>
        )}
      </button>
    );
  };

  const renderEncyclopedia = () => (
    <>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {([
          { mode: 'recent', label: '최근순' },
          { mode: 'name', label: '이름순' },
          { mode: 'tier', label: '티어순' }
        ] as const).map((item) => (
          <button
            key={item.mode}
            type="button"
            onClick={() => { playSfx('ui-tap'); dispatch({ type: 'SET_SORT_MODE', payload: { mode: item.mode } }); }}
            style={{
              flex: 1,
              borderRadius: '12px',
              border: `1px solid ${state.sortMode === item.mode ? COLORS.accent : COLORS.border}`,
              background: state.sortMode === item.mode ? COLORS.accent : COLORS.card,
              color: state.sortMode === item.mode ? '#fff' : COLORS.text,
              fontSize: '12px',
              fontWeight: 600,
              padding: '10px 4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '12px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: '2px'
        }}
      >
        <button
          type="button"
          onClick={() => { playSfx('ui-tap'); dispatch({ type: 'SET_CATEGORY_FILTER', payload: { filter: 'all' } }); }}
          style={{
            flexShrink: 0,
            borderRadius: '20px',
            border: `1px solid ${state.categoryFilter === 'all' ? COLORS.accent : COLORS.border}`,
            background: state.categoryFilter === 'all' ? COLORS.accent : COLORS.card,
            color: state.categoryFilter === 'all' ? '#fff' : COLORS.text,
            fontSize: '11px',
            fontWeight: 600,
            padding: '6px 12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          전체 {state.inventory.length}
        </button>
        {(Object.entries(CATEGORY_ICONS) as [ElementCategory, string][]).map(([cat, icon]) => {
          const count = categoryCounts[cat] ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => { playSfx('ui-tap'); dispatch({ type: 'SET_CATEGORY_FILTER', payload: { filter: cat } }); }}
              style={{
                flexShrink: 0,
                borderRadius: '20px',
                border: `1px solid ${state.categoryFilter === cat ? COLORS.accent : COLORS.border}`,
                background: state.categoryFilter === cat ? COLORS.accent : COLORS.card,
                color: state.categoryFilter === cat ? '#fff' : COLORS.text,
                fontSize: '11px',
                fontWeight: 600,
                padding: '6px 10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {icon} {cat} {count}
            </button>
          );
        })}
      </div>
      <div style={{ color: COLORS.textDim, fontSize: '12px', marginBottom: '12px', fontWeight: 500 }}>
        {nextMilestone
          ? `레벨 업까지 ${discoveredCount} / ${nextMilestone.threshold}`
          : `발견 ${discoveredCount}`}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '10px',
          paddingBottom: '20px'
        }}
      >
        {filteredInventory.map((name) => {
          const element = state.elements[name];
          const isFirstDiscoverer = state.firstDiscoveries.includes(name);

          return (
            <button
              key={name}
              type="button"
              onPointerDown={() => handleElementPointerDown(name)}
              onPointerUp={() => handleElementPointerUp(name)}
              onPointerLeave={handleElementPointerLeave}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                position: 'relative',
                border: `2px solid ${TIER_COLORS[element?.tier ?? 0] ?? COLORS.border}`,
                borderRadius: '16px',
                background: COLORS.card,
                color: COLORS.text,
                minHeight: '94px',
                padding: '10px 4px',
                cursor: state.pendingCombine ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 10px rgba(74, 66, 56, 0.03)',
                transition: 'transform 0.1s ease',
                animation: 'mago-scale-up 0.4s ease-out',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none'
              }}
            >
              {isFirstDiscoverer && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    left: '-6px',
                    background: COLORS.accent,
                    color: '#fff',
                    fontSize: '9px',
                    fontWeight: 800,
                    padding: '2px 5px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 6px rgba(212, 185, 150, 0.4)',
                    zIndex: 2,
                    border: '1px solid #fff',
                    whiteSpace: 'nowrap'
                  }}
                >
                  최초발견
                </div>
              )}
              {element?.discoveredCount != null && element.discoveredCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '6px',
                  right: '8px',
                  fontSize: '9px',
                  fontWeight: 600,
                  color: COLORS.textDim,
                  opacity: 0.8,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>{formatCount(element.discoveredCount)}명 발견</span>
              )}
              <div style={{ fontSize: '26px', marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>{renderIcon(element?.icon ?? '❔', element?.imageUrl, 36)}</div>
              <div style={{ fontSize: '12px', fontWeight: 700 }}>{name}</div>
            </button>
          );
        })}
        {Array.from({ length: Math.min(unknownSlots, 12) }).map((_, index) => (
          <div
            key={`unknown-${index}`}
            style={{
              border: `1px solid ${COLORS.border}`,
              borderRadius: '16px',
              background: COLORS.slotBg,
              minHeight: '94px',
              padding: '10px 4px',
              color: COLORS.textDim,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.6
            }}
          >
            <div style={{ fontSize: '24px', filter: 'grayscale(1)', opacity: 0.3 }}>🌫️</div>
            <div style={{ fontSize: '11px', fontWeight: 500 }}>미지</div>
          </div>
        ))}
      </div>
    </>
  );

  const renderRankingList = (entries: RankingEntry[], unit: string) => {
    if (entries.length === 0) {
      return (
        <div style={{ textAlign: 'center', color: COLORS.textDim, padding: '30px 0', fontSize: '14px' }}>
          아직 데이터가 없습니다.
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {entries.map((entry) => {
          const isMe = entry.nickname === state.nickname;
          return (
            <div
              key={`${entry.rank}-${entry.nickname}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                borderRadius: '14px',
                background: isMe ? COLORS.accentLight : COLORS.card,
                border: `1px solid ${isMe ? COLORS.accent : COLORS.border}`,
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                width: '30px',
                textAlign: 'center',
                fontSize: entry.rank <= 3 ? '18px' : '14px',
                fontWeight: 800,
                color: entry.rank === 1 ? '#f59f00' : entry.rank === 2 ? '#868e96' : entry.rank === 3 ? '#e67700' : COLORS.textDim,
                flexShrink: 0
              }}>
                {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
              </div>
              <div style={{ flex: 1, fontSize: '14px', fontWeight: isMe ? 800 : 600, color: COLORS.text }}>
                {entry.nickname}
                {isMe && <span style={{ marginLeft: '6px', fontSize: '11px', color: COLORS.accent, fontWeight: 700 }}>나</span>}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text, flexShrink: 0 }}>
                {entry.value.toLocaleString()}{unit}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRankings = () => {
    const subTabs = [
      { key: 'discoveries' as const, label: '발견왕', unit: '개' },
      { key: 'first_discoveries' as const, label: '개척자', unit: '개' },
      { key: 'max_depth' as const, label: '심연 탐험가', unit: '' }
    ];

    const currentSubTab = subTabs.find((t) => t.key === rankingsSubTab) ?? subTabs[0];
    const currentEntries = rankings?.[rankingsSubTab] ?? [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {subTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => { playSfx('ui-tap'); setRankingsSubTab(tab.key); }}
              style={{
                flex: 1,
                borderRadius: '12px',
                border: `1px solid ${rankingsSubTab === tab.key ? COLORS.accent : COLORS.border}`,
                background: rankingsSubTab === tab.key ? COLORS.accent : COLORS.card,
                color: rankingsSubTab === tab.key ? '#fff' : COLORS.text,
                fontSize: '12px',
                fontWeight: 600,
                padding: '10px 4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: COLORS.textDim, fontSize: '12px', fontWeight: 500 }}>
            {rankings ? `총 ${rankings.total_players}명 참여` : ''}
          </div>
          <button
            type="button"
            onClick={() => { playSfx('ui-tap'); loadRankings(); }}
            disabled={rankingsLoading}
            style={{
              border: `1px solid ${COLORS.border}`,
              borderRadius: '10px',
              background: COLORS.card,
              color: COLORS.textDim,
              fontSize: '12px',
              fontWeight: 600,
              padding: '6px 12px',
              cursor: rankingsLoading ? 'not-allowed' : 'pointer',
              opacity: rankingsLoading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {rankingsLoading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ ...spinnerStyle, width: '10px', height: '10px', borderWidth: '1.5px' }} />
                로딩
              </span>
            ) : '새로고침'}
          </button>
        </div>

        {!backendEnabled ? (
          <div style={{ textAlign: 'center', color: COLORS.textDim, padding: '40px 0', fontSize: '14px' }}>
            랭킹은 온라인 모드에서만 이용 가능합니다.
          </div>
        ) : rankingsLoading && !rankings ? (
          <div style={{ textAlign: 'center', color: COLORS.textDim, padding: '40px 0', fontSize: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <span style={{ ...spinnerStyle, width: '14px', height: '14px', borderWidth: '2px' }} />
            랭킹 불러오는 중...
          </div>
        ) : (
          renderRankingList(currentEntries, currentSubTab.unit)
        )}
      </div>
    );
  };

  const renderProfile = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '20px' }}>
      <div style={{ borderRadius: '16px', background: COLORS.accentLight, border: `1px solid ${COLORS.border}`, padding: '20px' }}>
        <div style={{ color: COLORS.text, fontWeight: 800, fontSize: '20px' }}>
          {state.nickname || '이름 없는 신'}
        </div>
        <div style={{ color: COLORS.textDim, fontSize: '13px', marginTop: '6px', fontWeight: 500 }}>
          당신은 이 우주의 창조주입니다.
        </div>
      </div>
      <div style={{ borderRadius: '16px', background: COLORS.slotBg, border: `1px solid ${COLORS.border}`, padding: '16px' }}>
        <div style={{ color: COLORS.text, fontSize: '14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span>총 발견 원소</span>
          <span style={{ fontWeight: 700 }}>{discoveredCount}</span>
        </div>
        <div style={{ color: COLORS.text, fontSize: '14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span>최초 발견 원소</span>
          <span style={{ fontWeight: 700 }}>{firstDiscoveryCount}</span>
        </div>
        <div style={{ color: COLORS.text, fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
          <span>플레이 시간</span>
          <span style={{ fontWeight: 700 }}>{Math.floor(state.playTime)}초</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowResetConfirm(true)}
        style={{
          marginTop: '8px',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          background: 'transparent',
          color: COLORS.textDim,
          fontSize: '13px',
          fontWeight: 500,
          padding: '12px',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        데이터 초기화
      </button>
    </div>
  );

  const renderWorkshop = () => (
    <div style={{ display: 'flex', flexDirection: 'column', padding: `0 0 ${showMissionBanner ? '150px' : '100px'} 0` }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: COLORS.bg,
          padding: '20px 0 16px 0',
          zIndex: 90,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        {state.screen === 'tutorial' && (
          <div
            style={{
              borderRadius: '16px',
              border: `1px solid ${COLORS.accent}`,
              background: COLORS.accentLight,
              color: COLORS.text,
              fontSize: '14px',
              padding: '12px 16px',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            입문: <strong>물 + 불 = 증기</strong>를 만들어보세요.
          </div>
        )}

        <div
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: '20px',
            padding: '20px',
            background: COLORS.slotBg,
            boxShadow: '0 4px 15px rgba(74, 66, 56, 0.05)',
            animation: state.slotA && state.slotB ? 'mago-pulse 2s infinite' : 'none'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ color: COLORS.textDim, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>조합의 제단</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px' }}>&#9889;</span>
              <div style={{ width: '60px', height: '8px', background: COLORS.border, borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${(state.energy / MAX_ENERGY) * 100}%`, height: '100%', background: state.energy > 0 ? COLORS.accent : '#e74c3c', borderRadius: '4px', transition: 'width 0.3s ease' }} />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: state.energy > 0 ? COLORS.text : '#e74c3c', minWidth: '36px' }}>
                {state.energy}/{MAX_ENERGY}
              </span>
              {energyTimerText && (
                <span style={{ fontSize: '11px', color: COLORS.textDim }}>({energyTimerText})</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {renderSlot('A', state.slotA, slotElementA)}
            <div style={{ color: COLORS.accent, fontWeight: 800, fontSize: '24px' }}>+</div>
            {renderSlot('B', state.slotB, slotElementB)}
          </div>
          <div style={{ marginTop: '14px', fontSize: '13px', color: COLORS.textDim, minHeight: '20px', textAlign: 'center', fontWeight: 500 }}>
            {state.pendingCombine
              ? state.pendingCombine.status === 'waiting-server'
                ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ ...spinnerStyle, width: '12px', height: '12px', borderWidth: '2px' }} />
                    우주의 기운을 모으는 중...
                  </span>
                )
                : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ ...spinnerStyle, width: '12px', height: '12px', borderWidth: '2px' }} />
                    합성 중...
                  </span>
                )
              : state.slotA && state.slotB
                ? '새로운 존재가 탄생합니다.'
                : '원소를 선택하여 제단에 올리세요.'}
          </div>
        </div>

        <div
          style={{
            borderRadius: '14px',
            border: `1px solid ${COLORS.border}`,
            background: COLORS.card,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 4px 12px rgba(74, 66, 56, 0.03)'
          }}
        >
          <span style={{ fontSize: '16px' }}>🔍</span>
          <input
            value={state.searchQuery}
            onChange={(event) =>
              dispatch({ type: 'SET_SEARCH_QUERY', payload: { query: event.target.value } })
            }
            placeholder="원소 이름 검색..."
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: COLORS.text,
              outline: 'none',
              fontSize: '15px',
              fontWeight: 500
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, marginTop: '10px' }}>
        {state.activeTab === 'encyclopedia' && renderEncyclopedia()}
        {state.activeTab === 'rankings' && renderRankings()}
        {state.activeTab === 'profile' && renderProfile()}
      </div>

      {showMissionBanner && (
        <div
          style={{
            position: 'fixed',
            bottom: '76px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'min(452px, calc(100% - 28px))',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)',
            border: '1px solid #f0d58c',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 20px rgba(212, 185, 150, 0.25)',
            zIndex: 99,
            animation: 'mago-glow 3s ease-in-out infinite, mago-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <span style={{ fontSize: '28px', flexShrink: 0, animation: 'mago-float 2s ease-in-out infinite' }}>🧑</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#5c4a1e', letterSpacing: '-0.02em' }}>
              궁극의 목표
            </div>
            <div style={{ fontSize: '12px', color: '#8c7a4a', fontWeight: 500, marginTop: '2px' }}>
              원소를 조합하여 "인간"을 만들어 보세요!
            </div>
          </div>
          <span style={{ fontSize: '16px', color: '#c9a84c', flexShrink: 0 }}>▶</span>
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(452px, calc(100% - 28px))',
          borderRadius: '18px',
          border: `1px solid ${COLORS.border}`,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          overflow: 'hidden',
          padding: '4px',
          boxShadow: '0 8px 32px rgba(74, 66, 56, 0.12)',
          zIndex: 100,
          animation: 'mago-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {([
          { key: 'encyclopedia', label: '도감' },
          { key: 'rankings', label: '랭킹' },
          { key: 'profile', label: '신정보' }
        ] as const).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => { playSfx('ui-tap'); dispatch({ type: 'SET_ACTIVE_TAB', payload: { tab: tab.key } }); }}
            style={{
              border: 'none',
              borderRadius: '14px',
              padding: '12px 6px',
              background: state.activeTab === tab.key ? COLORS.card : 'transparent',
              color: state.activeTab === tab.key ? COLORS.text : COLORS.textDim,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: state.activeTab === tab.key ? '0 4px 10px rgba(74, 66, 56, 0.05)' : 'none'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderGlobalOverlays = () => {
    return (
      <>
        {nicknameConfirmTarget && (
          <div style={overlayBackdropStyle}>
            <div style={dialogCardStyle}>
              <div style={dialogTitleStyle}>닉네임 확정</div>
              <div style={dialogBodyStyle}>정말 {nicknameConfirmTarget}(으)로 하시겠습니까?</div>
              <div style={dialogActionsStyle}>
                <button
                  type="button"
                  onClick={() => { playSfx('ui-tap'); setNicknameConfirmTarget(null); }}
                  style={{ ...dialogButtonStyle, background: COLORS.textDim }}
                  disabled={isRegisteringNickname}
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => { playSfx('ui-tap'); confirmNickname(); }}
                  style={{ ...dialogButtonStyle, background: COLORS.accent }}
                  disabled={isRegisteringNickname}
                >
                  {isRegisteringNickname ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span style={spinnerStyle} />
                      기록 중...
                    </span>
                  ) : (
                    '확인'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {showEnergyDialog && (
          <div style={overlayBackdropStyle}>
            <div style={dialogCardStyle}>
              <div style={{ fontSize: '40px', textAlign: 'center', marginBottom: '8px' }}>&#9889;</div>
              <div style={dialogTitleStyle}>에너지 부족</div>
              <div style={dialogBodyStyle}>
                합성에 필요한 에너지가 없습니다.
                {energyTimerText && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: COLORS.textDim }}>
                    다음 충전까지 {energyTimerText}
                  </div>
                )}
              </div>
              <div style={{ ...dialogActionsStyle, flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  disabled={isWatchingAd}
                  onClick={async () => {
                    setIsWatchingAd(true);
                    // BGM 일시정지
                    const bgm = bgmRef.current;
                    let wasBgmPlaying = false;
                    let bgmPauseOffset = 0;
                    if (bgm && bgm.playing) {
                      wasBgmPlaying = true;
                      bgmPauseOffset = bgm.ctx.currentTime - bgm.startTime;
                      if (bgm.source) {
                        try { bgm.source.stop(); } catch { /* */ }
                        bgm.source.disconnect();
                        bgm.source = null;
                      }
                      bgm.playing = false;
                    }

                    const earned = await showRewarded();

                    // BGM 재개
                    if (wasBgmPlaying && bgm && bgm.currentPath) {
                      const buffer = bgm.buffers.get(bgm.currentPath);
                      if (buffer && bgm.ctx) {
                        if (bgm.ctx.state === 'suspended') await bgm.ctx.resume();
                        const src = bgm.ctx.createBufferSource();
                        src.buffer = buffer;
                        src.loop = true;
                        src.connect(bgm.gain);
                        const offset = bgmPauseOffset % buffer.duration;
                        src.start(0, offset);
                        bgm.source = src;
                        bgm.startTime = bgm.ctx.currentTime - offset;
                        bgm.pauseOffset = 0;
                        bgm.playing = true;
                      }
                    }

                    if (earned) {
                      dispatch({ type: 'RECHARGE_ENERGY', payload: { amount: MAX_ENERGY } });
                    }
                    setIsWatchingAd(false);
                    setShowEnergyDialog(false);
                  }}
                  style={{ ...dialogButtonStyle, background: COLORS.accent, width: '100%', padding: '14px', fontSize: '15px' }}
                >
                  {isWatchingAd ? '광고 재생 중...' : '광고 보고 충전하기'}
                </button>
                <button
                  type="button"
                  onClick={() => { playSfx('ui-tap'); setShowEnergyDialog(false); }}
                  style={{ ...dialogButtonStyle, background: 'transparent', color: COLORS.textDim, border: `1px solid ${COLORS.border}`, width: '100%' }}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}
        {dialogMessage && (
          <div style={overlayBackdropStyle}>
            <div style={dialogCardStyle}>
              <div style={dialogTitleStyle}>{dialogMessage.title}</div>
              <div style={dialogBodyStyle}>{dialogMessage.message}</div>
              <div style={dialogActionsStyle}>
                <button
                  type="button"
                  onClick={() => { playSfx('ui-tap'); setDialogMessage(null); }}
                  style={{ ...dialogButtonStyle, background: COLORS.accent }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
        {detailElement && (() => {
          const el = state.elements[detailElement];
          if (!el) return null;
          const isFirstDiscoverer = state.firstDiscoveries.includes(detailElement);
          return (
            <div
              style={overlayBackdropStyle}
              onClick={() => setDetailElement(null)}
            >
              <div
                style={{
                  ...dialogCardStyle,
                  padding: '28px 24px',
                  animation: 'mago-bounce-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                  {renderIcon(el.icon, el.imageUrl, 64)}
                </div>
                <div style={{ color: COLORS.text, fontSize: '22px', fontWeight: 800, textAlign: 'center' }}>{el.name}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
                  <span style={{
                    fontSize: '12px', fontWeight: 600, padding: '3px 10px',
                    background: TIER_COLORS[el.tier] ?? COLORS.slotBg, borderRadius: '16px', color: '#fff'
                  }}>등급 {el.tier}</span>
                  <span style={{
                    fontSize: '12px', fontWeight: 600, padding: '3px 10px',
                    background: COLORS.accentLight, borderRadius: '16px', color: COLORS.text,
                    border: `1px solid ${COLORS.border}`
                  }}>{CATEGORY_ICONS[el.category]} {el.category}</span>
                </div>
                {el.description && (
                  <div style={{
                    marginTop: '20px',
                    padding: '16px 20px',
                    background: COLORS.accentLight,
                    borderRadius: '14px',
                    borderLeft: `3px solid ${COLORS.accent}`,
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute', top: '8px', left: '12px',
                      fontSize: '28px', color: COLORS.accent, opacity: 0.3, fontFamily: 'Georgia, serif', lineHeight: 1
                    }}>"</div>
                    <div style={{
                      color: COLORS.text, fontSize: '14px', fontWeight: 500,
                      lineHeight: 1.7, fontStyle: 'italic', textAlign: 'center',
                      padding: '4px 8px'
                    }}>
                      {el.description}
                    </div>
                  </div>
                )}
                <div style={{
                  marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '6px',
                  fontSize: '13px', color: COLORS.textDim
                }}>
                  {el.depth != null && el.depth > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>합성 깊이</span>
                      <span style={{ fontWeight: 700, color: COLORS.text }}>{el.depth}</span>
                    </div>
                  )}
                  {el.discoveredCount != null && el.discoveredCount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>발견한 사람 수</span>
                      <span style={{ fontWeight: 700, color: COLORS.text }}>{el.discoveredCount}명</span>
                    </div>
                  )}
                  {isFirstDiscoverer && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>최초 발견자</span>
                      <span style={{ fontWeight: 700, color: COLORS.accent }}>{state.nickname}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { playSfx('popup-close'); setDetailElement(null); }}
                  style={{ ...dialogButtonStyle, background: COLORS.accent, marginTop: '20px', width: '100%' }}
                >
                  닫기
                </button>
              </div>
            </div>
          );
        })()}
        {showResetConfirm && (
          <div style={overlayBackdropStyle}>
            <div style={dialogCardStyle}>
              <div style={dialogTitleStyle}>데이터 초기화</div>
              <div style={dialogBodyStyle}>
                모든 발견 기록과 진행 상황이 삭제됩니다.{'\n'}
                초기화 후 동일한 닉네임은 사용할 수 없습니다.
              </div>
              <div style={dialogActionsStyle}>
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(false)}
                  style={{ ...dialogButtonStyle, background: COLORS.textDim }}
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.localStorage.removeItem(LOCAL_PROFILE_STORAGE_KEY);
                    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
                    setShowResetConfirm(false);
                    dispatch({ type: 'RESET' });
                  }}
                  style={{ ...dialogButtonStyle, background: '#e03131' }}
                >
                  초기화
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderRuntimeStyles = () => (
    <style>
      {`
        @keyframes mago-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes mago-bounce-in {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); opacity: 1; }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        @keyframes mago-scale-up {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes mago-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mago-pulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(212, 185, 150, 0.4); }
          70% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(212, 185, 150, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(212, 185, 150, 0); }
        }
        @keyframes mago-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes mago-glow {
          0%, 100% { filter: drop-shadow(0 0 2px rgba(212, 185, 150, 0.3)); }
          50% { filter: drop-shadow(0 0 8px rgba(212, 185, 150, 0.6)); }
        }
        @keyframes mago-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes mago-page-turn {
          0% { 
            transform: perspective(1200px) rotateY(-15deg) translateX(40px) scale(0.95); 
            opacity: 0; 
            filter: brightness(0.8);
          }
          100% { 
            transform: perspective(1200px) rotateY(0deg) translateX(0) scale(1); 
            opacity: 1;
            filter: brightness(1);
          }
        }
        @keyframes video-fade-out {
          0% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes delayed-fade-in {
          0% { opacity: 0; transform: translateY(10px); }
          85% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}
    </style>
  );

  if (state.screen === 'title') {
    return (
      <>
        {renderRuntimeStyles()}
        <div style={{ ...shellStyle, position: 'relative', overflow: 'hidden', padding: 0 }}>
          {/* 비디오 배경 컨테이너 (너비 제한) */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <video
              autoPlay
              muted
              playsInline
              loop
              style={{
                width: '100%',
                maxWidth: '480px',
                height: '100%',
                objectFit: 'cover',
                animation: 'video-fade-out 6s forwards'
              }}
            >
              <source src="/title-video.mp4" type="video/mp4" />
            </video>
          </div>

          <div 
            style={{ 
              ...contentContainerStyle, 
              justifyContent: 'space-between', 
              textAlign: 'center', 
              maxWidth: '360px',
              position: 'relative',
              zIndex: 10,
              padding: '60px 20px calc(40px + env(safe-area-inset-bottom)) 20px',
              height: '100svh'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <img 
                src="/title-logo.png" 
                alt="창조신 김마고" 
                style={{ 
                  width: '280px', 
                  maxWidth: '100%', 
                  height: 'auto',
                  animation: 'delayed-fade-in 6s forwards'
                }} 
              />
              <div 
                style={{ 
                  fontSize: '16px', 
                  color: COLORS.textDim, 
                  fontWeight: 600, 
                  lineHeight: 1.6,
                  letterSpacing: '0.05em',
                  animation: 'delayed-fade-in 6s forwards'
                }}
              >
                태초의 원소를 조합하여<br />당신만의 세계를 완성하세요
              </div>
            </div>

            <button
              type="button"
              onClick={() => { if (!canStartFromTitle) return; playSfx('ui-tap'); dispatch({ type: 'START_FROM_TITLE' }); }}
              style={{
                ...mainButtonStyle,
                boxShadow: '0 12px 40px rgba(212, 185, 150, 0.45)',
                border: '1px solid rgba(255,255,255,0.4)',
                background: COLORS.accent,
                backdropFilter: 'blur(4px)',
                padding: '18px 20px',
                fontSize: '18px',
                letterSpacing: '0.1em'
              }}
            >
              세상의 문을 열기
            </button>
          </div>
        </div>
        {renderGlobalOverlays()}
      </>
    );
  }

  if (state.screen === 'opening') {
    return (
      <>
        {renderRuntimeStyles()}
        <div
          style={{
            width: '100%',
            height: '100svh',
            background: COLORS.bg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              height: 'calc(100svh - 40px - env(safe-area-inset-bottom))',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
            onClick={() => { playSfx('ui-tap'); dispatch({ type: 'ADVANCE_OPENING' }); }}
          >
            <img
              key={state.openingPage}
              src={OPENING_PAGES[state.openingPage]}
              alt={`만화 ${state.openingPage + 1}`}
              style={{
                height: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
                animation: 'mago-page-turn 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
              }}
            />
          </div>
          <div
            style={{
              height: '40px',
              paddingBottom: 'env(safe-area-inset-bottom)',
              flexShrink: 0,
              paddingLeft: '20px',
              paddingRight: '20px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              background: COLORS.card,
              borderTop: `1px solid ${COLORS.border}`
            }}
          >
            <button
              type="button"
              style={{
                background: 'transparent',
                border: 'none',
                color: COLORS.text,
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                height: '100%'
              }}
              onClick={(e) => { e.stopPropagation(); playSfx('ui-tap'); dispatch({ type: 'ADVANCE_OPENING' }); }}
            >
              <span style={{ display: 'flex', gap: '6px' }}>
                {OPENING_PAGES.map((_, i) => (
                  <span
                    key={i}
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: i === state.openingPage ? COLORS.accent : COLORS.border
                    }}
                  />
                ))}
              </span>
              {state.openingPage < OPENING_PAGES.length - 1 ? '다음' : '시작하기'}
            </button>
          </div>
        </div>
        {renderGlobalOverlays()}
      </>
    );
  }

  if (state.screen === 'nickname') {
    return (
      <>
        {renderRuntimeStyles()}
        <div style={shellStyle}>
          <div style={{ ...contentContainerStyle, justifyContent: 'center', maxWidth: '400px' }}>
            <div style={{ color: COLORS.text, fontSize: '32px', fontWeight: 800, textAlign: 'center', letterSpacing: '-0.02em' }}>신의 이름</div>
            <div style={{ color: COLORS.textDim, fontSize: '15px', marginTop: '12px', lineHeight: 1.6, textAlign: 'center' }}>
              당신이 불릴 이름을 정해주세요.<br />
              이 이름은 우주에 영원히 새겨집니다.
            </div>
            <input
              value={state.nicknameDraft}
              onChange={(event) =>
                dispatch({ type: 'SET_NICKNAME_DRAFT', payload: { value: event.target.value } })
              }
              placeholder="2~8자, 한글/영문/숫자"
              disabled={isRegisteringNickname}
              style={{
                marginTop: '40px',
                width: '100%',
                borderRadius: '20px',
                border: `1px solid ${COLORS.border}`,
                background: COLORS.card,
                color: COLORS.text,
                fontSize: '20px',
                padding: '20px',
                outline: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                opacity: isRegisteringNickname ? 0.75 : 1,
                boxShadow: '0 4px 12px rgba(74, 66, 56, 0.05)'
              }}
            />
            <div style={{ marginTop: '16px', minHeight: '18px', fontSize: '14px', textAlign: 'center', color: isNicknameValid ? '#74b816' : '#f03e3e', fontWeight: 500 }}>
              {state.nicknameDraft.length === 0
                ? backendEnabled
                  ? '서버에서 중복 확인 후 등록됩니다.'
                  : '로컬 모드로 시작됩니다.'
                : isNicknameValid
                  ? '사용 가능한 이름입니다.'
                  : '2~8자 한글, 영문, 숫자만 가능합니다.'}
            </div>
            <button
              type="button"
              disabled={!isNicknameValid || isRegisteringNickname}
              onClick={submitNickname}
              style={{
                ...mainButtonStyle,
                marginTop: '30px',
                opacity: isNicknameValid && !isRegisteringNickname ? 1 : 0.5,
                cursor: isNicknameValid && !isRegisteringNickname ? 'pointer' : 'not-allowed'
              }}
            >
              {isRegisteringNickname ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <span style={spinnerStyle} />
                  기록 중...
                </span>
              ) : (
                '이름 새기기'
              )}
            </button>
          </div>
        </div>
        {renderGlobalOverlays()}
      </>
    );
  }

  return (
    <>
      {renderRuntimeStyles()}
      <div style={shellStyle}>
        <div style={contentContainerStyle}>
          {renderWorkshop()}
        </div>
        {popup && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(74, 66, 56, 0.2)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              zIndex: 6000,
              animation: 'mago-fade-in 0.3s ease-out'
            }}
          >
            <div
              style={{
                width: 'min(340px, 100%)',
                borderRadius: '24px',
                background: COLORS.card,
                padding: '30px 20px',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(74, 66, 56, 0.15)',
                border: `1px solid ${COLORS.border}`,
                animation: 'mago-bounce-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              <div style={{ color: COLORS.text, fontSize: '24px', fontWeight: 800 }}>{popupTitle}</div>
              <div style={{
                marginTop: '20px',
                fontSize: '50px',
                animation: 'mago-float 2s ease-in-out infinite',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {renderIcon(popup.result.icon, popup.result.imageUrl, 72)}
              </div>
              <div style={{ color: COLORS.text, fontSize: '26px', fontWeight: 800, marginTop: '10px' }}>{popup.result.name}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '6px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: TIER_COLORS[popup.result.tier] ?? COLORS.slotBg,
                  borderRadius: '20px',
                  color: '#fff'
                }}>
                  등급 {popup.result.tier}
                </div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: COLORS.accentLight,
                  borderRadius: '20px',
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`
                }}>
                  {CATEGORY_ICONS[popup.result.category]} {popup.result.category}
                </div>
              </div>
              {popup.result.description && (
                <div style={{
                  marginTop: '14px',
                  padding: '10px 16px',
                  background: COLORS.accentLight,
                  borderRadius: '12px',
                  borderLeft: `3px solid ${COLORS.accent}`
                }}>
                  <div style={{
                    color: COLORS.text, fontSize: '13px', fontWeight: 500,
                    lineHeight: 1.6, fontStyle: 'italic', textAlign: 'center'
                  }}>
                    "{popup.result.description}"
                  </div>
                </div>
              )}
              {popup.kind === 'milestone' && popup.milestoneDescription && (
                <div style={{ marginTop: '16px', color: COLORS.text, fontSize: '15px', fontWeight: 600, lineHeight: 1.6 }}>
                  {popup.milestoneDescription}
                </div>
              )}
              {popup.kind === 'first-discovery' && (
                <div style={{ marginTop: '16px', color: COLORS.textDim, fontSize: '14px', lineHeight: 1.6 }}>
                  이 존재의 <strong>최초 발견자</strong>로 기록됩니다:
                  <br />
                  <span style={{ color: COLORS.text, fontWeight: 700 }}>{popup.firstDiscoverer}</span>
                </div>
              )}
              {popup.kind === 'discovered' && popup.firstDiscoverer && popup.firstDiscoverer !== state.nickname && (
                <div style={{ marginTop: '16px', color: COLORS.textDim, fontSize: '13px', lineHeight: 1.6 }}>
                  이 존재의 최초 발견자는 <span style={{ color: COLORS.text, fontWeight: 700 }}>{popup.firstDiscoverer}</span>입니다.
                </div>
              )}
              <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
                {popup.kind === 'first-discovery' && (
                  <button type="button" onClick={() => { playSfx('ui-tap'); shareFirstDiscovery(); }} style={{ ...popupButtonStyle, background: COLORS.accentDim || '#b8a68d' }}>
                    기록 공유
                  </button>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    playSfx('popup-close');
                    dispatch({ type: 'CLOSE_RESULT_POPUP' });

                    // 마일스톤 달성 시 전면광고 표시
                    if (popup.kind === 'milestone') {
                      await showInterstitial();
                    }
                  }}
                  style={{ ...popupButtonStyle, background: COLORS.accent }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {renderGlobalOverlays()}
    </>
  );
}

const shellStyle: CSSProperties = {
  width: '100%',
  minHeight: '100vh',
  background: COLORS.bg,
  color: COLORS.text,
  fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0 14px',
  boxSizing: 'border-box'
};

const contentContainerStyle: CSSProperties = {
  width: 'min(480px, 100%)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  boxSizing: 'border-box'
};

const mainButtonStyle: CSSProperties = {
  marginTop: '24px',
  border: 'none',
  borderRadius: '16px',
  background: COLORS.accent,
  color: '#fff',
  fontSize: '17px',
  fontWeight: 700,
  padding: '16px 20px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  width: '100%'
};

const popupButtonStyle: CSSProperties = {
  flex: 1,
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  padding: '12px 8px',
  fontWeight: 700,
  cursor: 'pointer'
};

const overlayBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(74, 66, 56, 0.15)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  zIndex: 5000
};

const dialogCardStyle: CSSProperties = {
  width: 'min(340px, 100%)',
  borderRadius: '20px',
  background: COLORS.card,
  padding: '24px 20px',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(74, 66, 56, 0.1)'
};

const dialogTitleStyle: CSSProperties = {
  color: COLORS.text,
  fontSize: '20px',
  fontWeight: 800
};

const dialogBodyStyle: CSSProperties = {
  marginTop: '12px',
  color: COLORS.textDim,
  fontSize: '14px',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap'
};

const dialogActionsStyle: CSSProperties = {
  marginTop: '20px',
  display: 'flex',
  gap: '10px'
};

const dialogButtonStyle: CSSProperties = {
  flex: 1,
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  padding: '12px 8px',
  fontWeight: 700,
  cursor: 'pointer'
};

const spinnerStyle: CSSProperties = {
  width: '14px',
  height: '14px',
  borderRadius: '999px',
  border: `2px solid rgba(74, 66, 56, 0.1)`,
  borderTopColor: COLORS.accent,
  animation: 'mago-spin 0.8s linear infinite',
  display: 'inline-block'
};

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function parseLocalProfileSnapshot(raw: string | null): LocalProfileSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LocalProfileSnapshot>;
    if (!parsed || typeof parsed.nickname !== 'string' || parsed.nickname.length === 0) return null;
    return {
      nickname: parsed.nickname,
      authToken: typeof parsed.authToken === 'string' ? parsed.authToken : null,
      inventory: Array.isArray(parsed.inventory)
        ? parsed.inventory.filter((item): item is string => typeof item === 'string')
        : [],
      hasSeenOpening: typeof parsed.hasSeenOpening === 'boolean' ? parsed.hasSeenOpening : true,
      tutorialCompleted: typeof parsed.tutorialCompleted === 'boolean' ? parsed.tutorialCompleted : true
    };
  } catch {
    return null;
  }
}
